import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { SYSTEM_GLOBAL_BANNER_REFRESH_EVENT } from 'utils/systemGlobalBannerRefresh';

/** מקור JSON להודעת סטטוס מערכת (גייסט) */
export const SYSTEM_STATUS_URL =
  'https://gist.githubusercontent.com/funnerapps/2d90fdee79beaea93c469f22d0f01994/raw/system-status.json';

const DISMISS_STORAGE_KEY = 'berrySystemGlobalBannerDismissedId';
const DEFAULT_POLL_MS = 300_000;

export type SystemStatusPayload = {
  id?: string;
  active?: boolean;
  config?: {
    severity?: string;
    icon?: string;
    closable?: boolean;
    /** כש־true: סגירה עם X לא נשמרת ב־localStorage; הבאנר חוזר אחרי כל טעינת JSON מוצלחת (polling / רענון) */
    ignoreDismiss?: boolean;
    pollingInterval?: number;
  };
  content?: {
    title?: string;
    message?: string;
    link?: { text?: string; url?: string };
  };
};

function stripLeadingUnderscoreKeys<T extends Record<string, unknown>>(obj: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    if (key.startsWith('_')) continue;
    out[key] = obj[key];
  }
  return out;
}

function isActiveTrue(v: unknown): boolean {
  return v === true || v === 'true' || v === 1 || v === '1';
}

function parsePayload(raw: unknown): SystemStatusPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const cleaned = stripLeadingUnderscoreKeys(raw as Record<string, unknown>);
  const id = cleaned.id;
  if (typeof id !== 'string' || !id.trim()) return null;
  if (!isActiveTrue(cleaned.active)) return null;

  const config = cleaned.config;
  if (!config || typeof config !== 'object') return null;

  const content = cleaned.content;
  if (!content || typeof content !== 'object') return null;

  return {
    id,
    active: true,
    config: config as SystemStatusPayload['config'],
    content: content as SystemStatusPayload['content'],
  };
}

type SystemGlobalBannerContextValue = {
  payload: SystemStatusPayload | null;
  /** האם המשתמש סגר את הבאנר הנוכחי (כולל מצב ignoreDismiss שמחזיק רק עד לטעינה הבאה) */
  isBannerDismissed: boolean;
  setDismissedForId: (id: string) => void;
  load: () => Promise<void>;
};

const SystemGlobalBannerContext = createContext<SystemGlobalBannerContextValue | null>(null);

export function useSystemGlobalBanner(): SystemGlobalBannerContextValue {
  const ctx = useContext(SystemGlobalBannerContext);
  if (!ctx) {
    throw new Error('useSystemGlobalBanner must be used within SystemGlobalBannerProvider');
  }
  return ctx;
}

export function SystemGlobalBannerProvider({ children }: { children: ReactNode }) {
  const pollMsRef = useRef(DEFAULT_POLL_MS);
  const [payload, setPayload] = useState<SystemStatusPayload | null>(null);
  const [dismissedId, setDismissedId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(DISMISS_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  /** סגירה זמנית כש־ignoreDismiss — מתאפס בכל load() מוצלח */
  const [sessionDismissedId, setSessionDismissedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const url = `${SYSTEM_STATUS_URL}?cb=${Date.now()}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) {
        setPayload(null);
        return;
      }
      const json: unknown = await res.json();
      const parsed = parsePayload(json);
      setPayload(parsed);
      if (parsed?.config?.ignoreDismiss === true) {
        setSessionDismissedId(null);
      }
      if (parsed?.config?.pollingInterval && parsed.config.pollingInterval > 0) {
        pollMsRef.current = parsed.config.pollingInterval;
      } else {
        pollMsRef.current = DEFAULT_POLL_MS;
      }
    } catch {
      setPayload(null);
    }
  }, []);

  /** טעינה ראשונית + polling רקורסיבי (אינטרוול מה־JSON אחרי כל טעינה) */
  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | undefined;

    const scheduleNext = () => {
      if (cancelled) return;
      timeoutId = window.setTimeout(runLoop, pollMsRef.current);
    };

    const runLoop = async () => {
      await load();
      if (cancelled) return;
      scheduleNext();
    };

    void runLoop();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [load]);

  /** רענון ידני מהדף / כפתור */
  useEffect(() => {
    const onManualRefresh = async (e: Event) => {
      const detail = (e as CustomEvent<{ resolve?: () => void; clearDismiss?: boolean }>).detail;
      const resolve = detail?.resolve;
      const clearDismiss = detail?.clearDismiss !== false;
      try {
        if (clearDismiss) {
          try {
            localStorage.removeItem(DISMISS_STORAGE_KEY);
          } catch {
            /* ignore */
          }
          setDismissedId(null);
          setSessionDismissedId(null);
        }
        await load();
      } finally {
        resolve?.();
      }
    };
    window.addEventListener(SYSTEM_GLOBAL_BANNER_REFRESH_EVENT, onManualRefresh as EventListener);
    return () => {
      window.removeEventListener(SYSTEM_GLOBAL_BANNER_REFRESH_EVENT, onManualRefresh as EventListener);
    };
  }, [load]);

  /** רענון כשחוזרים לטאב / לחלון — מוגבל פעם ב־~45 שניות כדי לא להציף את ה-gist */
  useEffect(() => {
    let lastExtraFetch = 0;
    const throttleMs = 45_000;
    const maybeLoad = () => {
      const now = Date.now();
      if (now - lastExtraFetch < throttleMs) return;
      lastExtraFetch = now;
      void load();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') maybeLoad();
    };
    const onFocus = () => maybeLoad();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  const setDismissedForId = useCallback(
    (id: string) => {
      if (payload?.config?.ignoreDismiss === true) {
        setSessionDismissedId(id);
        return;
      }
      try {
        localStorage.setItem(DISMISS_STORAGE_KEY, id);
      } catch {
        /* ignore */
      }
      setDismissedId(id);
    },
    [payload?.config?.ignoreDismiss],
  );

  const isBannerDismissed = useMemo(() => {
    if (!payload?.id) return false;
    if (payload.config?.ignoreDismiss === true) {
      return sessionDismissedId === payload.id;
    }
    return dismissedId === payload.id;
  }, [payload, dismissedId, sessionDismissedId]);

  const value = useMemo(
    () => ({
      payload,
      isBannerDismissed,
      setDismissedForId,
      load,
    }),
    [payload, isBannerDismissed, setDismissedForId, load],
  );

  return <SystemGlobalBannerContext.Provider value={value}>{children}</SystemGlobalBannerContext.Provider>;
}
